// Per-user namespacing for the browser-local overlay stores.
//
// The leads themselves are a SHARED, read-only source (the same AWS-backed
// dataset every workspace user sees, cached in the global IndexedDB `revailosales-leads`
// mirror). Everything a user *does* on top of that — segments, intent/outcomes,
// drafts — is their own overlay and must never leak between accounts or touch
// the leads. Those stores key their localStorage on the signed-in email, so each
// user (and each company) gets an isolated sandbox over the one shared lead sheet.

const SESSION_KEY = 'revailosales.session'

// Which account adopted each pre-namespacing global key (see adoptLegacy).
// Deliberately global, and deliberately NOT cleared on logout: this ledger is
// the only thing stopping the second account on a shared browser from
// inheriting the first account's data.
const ADOPTED_KEY = 'revailosales.legacyAdopted'

function readEmail() {
  try {
    return (JSON.parse(localStorage.getItem(SESSION_KEY))?.email || '').trim().toLowerCase()
  } catch {
    return ''
  }
}

// Initialize from the persisted session so scoped stores are correct on the very
// first read, before AuthProvider mounts.
let scope = readEmail() || 'anon'

// Base keys already checked for legacy data under the current scope. Purely a
// perf guard: scopedKey() sits on render-hot paths (isEnriched runs once per
// visible row), and without this the adoption check would hit localStorage on
// every call.
let checked = new Set()

export function setUserScope(email) {
  const next = (email || '').trim().toLowerCase() || 'anon'
  if (next === scope) return
  scope = next
  // A sign-out/sign-in without a reload must re-run adoption against the new
  // identity rather than trust the previous user's results.
  checked = new Set()
}

export function currentScope() {
  return scope
}

/**
 * The REAL signed-in account's address — the one the Cognito token belongs to
 * — as distinct from currentScope(), which impersonation points at the member
 * being viewed. Read off the persisted session, never off `scope`: the whole
 * point of exposing it is to let a store tell "this scope is mine" from "this
 * scope is someone I am viewing as", and only the session can say which.
 * '' when nobody is signed in.
 */
export function sessionEmail() {
  return readEmail()
}

function readAdopted() {
  try {
    const v = JSON.parse(localStorage.getItem(ADOPTED_KEY))
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {}
  } catch {
    return {}
  }
}

// MIGRATION RULE — one legacy blob, one owner.
//
// These stores were global before they were namespaced, so a browser that has
// been in use still holds real work under the bare key. The FIRST signed-in
// account to read a given base adopts it; every account after that starts
// empty. Handing the same blob to a second account is precisely the leak the
// namespacing exists to prevent, so "first one wins" is the whole point — not
// a tie-break detail.
//
// The legacy key is never deleted. If the wrong account happens to sign in
// first, the data is still recoverable by hand instead of gone.
function adoptLegacy(base, key) {
  try {
    const adopted = readAdopted()
    if (base in adopted) return
    const legacy = localStorage.getItem(base)
    // Claim the base on first touch WHETHER OR NOT there is data there yet.
    //   - Data present, and this user has no store of their own → take it.
    //   - Data present, but this user already has a store → claim without
    //     copying; overwriting would destroy their work.
    //   - No data at all → still claim. An unclaimed base stays adoptable
    //     forever, so anything that lands under the bare key LATER would be
    //     handed to whoever signs in next. That is not hypothetical: a module
    //     singleton that outlives the React tree (the enrichment poller) can
    //     write while signed out, and used to manufacture exactly such a blob.
    if (legacy !== null && localStorage.getItem(key) === null) {
      localStorage.setItem(key, legacy)
    }
    adopted[base] = scope
    localStorage.setItem(ADOPTED_KEY, JSON.stringify(adopted))
  } catch {
    /* private mode or quota — worst case the store just starts empty */
  }
}

// A per-user localStorage key, resolved against whoever is signed in RIGHT NOW
// — callers must call this per read/write, never cache the result past a
// possible sign-out. A signed-in user gets `<base>::<email>`, adopting any
// legacy global data on the first touch.
//
// Signed out, this returns `<base>::anon` — NOT the bare base key. The bare key
// is the legacy namespace, and adoptLegacy() hands it to the first account that
// signs in; returning it here meant any write made while signed out (the
// enrichment poller keeps running after logout) became a blob the next account
// inherited. Nothing adopts `::anon`.
export function scopedKey(base) {
  if (scope === 'anon') return `${base}::anon`
  const key = `${base}::${scope}`
  if (!checked.has(base)) {
    checked.add(base)
    adoptLegacy(base, key)
  }
  return key
}
